/**
 * B2 — new project, as a drawer over B1 (chat1.md :659).
 *
 * **One field: the name** (`KLQ-20261023-011`, 2026-10-28). A person who presses "new
 * project" wants a place to put drawings, and every other question asked at that moment is
 * one more decision between them and the first PDF. Client, address, the norm and its phases
 * are all `PATCH`-able later — project settings for the first two, the phase bar for the
 * phases — so none of them is a decision the person is stuck with.
 *
 * **The phases are not asked, and nothing about them is sent.** With no `norm_version_id` and
 * no `phase_definition_ids` the server resolves the studio's default norm and seeds every
 * mainline rung (`projects.service.seed_project_phases`) — exactly the set this drawer used to
 * pre-tick, so dropping the fieldset changed no project's phases. The first moment a phase
 * actually matters is the first transmittal, and the phase bar is where it is edited.
 *
 * **The idempotency key is minted per submission, not per drawer and not per click.** The
 * server fingerprints the request body against the key: replaying the same key with the same
 * body returns the first project (which is what makes a double-click or a retried request
 * safe), and replaying it with a *different* body is `409 idempotency_key_reused`. So the key
 * is cleared the moment any field changes — an edited retry is a new submission and gets a
 * new key, while an unedited one keeps its own.
 *
 * A `409 conflict` lands on the name field rather than above the form. It has exactly one
 * cause on this endpoint — `uq_project_workspace_name` — and a name that is taken is a fact
 * about the box the person is looking at. See `problems.ts`.
 *
 * **The company field appears only for somebody who could file the project under two of
 * them** (`KLQ-20260819-007` §3). B1 spans memberships by design, so the list a person is
 * looking at does not say which firm they mean — and a project's owning workspace is
 * immutable, so an implicit answer is a mistake with no screen to correct it. For the ~100%
 * of people with one company there is nothing to ask, and asking would be a field with one
 * option.
 *
 * Only workspaces where the caller holds `admin` or `manager` are offered: `Action.
 * create_project` sits at `MANAGER`, so a `member`'s own studio in that list would be an
 * option that answers `403`.
 *
 * **On success the caller decides what happens next** (`KLQ-20260825-007`, 2026-08-26).
 * `onCreated` receives the new project and, when it is given, it *replaces* `onClose` on the
 * success path — the caller is taking the person somewhere, and closing a drawer on a screen
 * that is being navigated away from is a state change nobody sees. Today that somewhere is
 * the project's drawings screen, ready for the first PDF (`KLQ-20261023-011`) — never the
 * shape wizard: the project is created with its root node, so it is a real tree with one
 * node, and its structure is offered later, when the drawings need it. A caller that only
 * wants the drawer shut passes `onClose` alone, and the behaviour is what it always was.
 */

import { useRef, useState } from 'react';

import { Button, Drawer, InlineError, Input, Select } from '@/components';
import { isCompany, useActiveWorkspaceId, useMe } from '@/features/session';
import type { MessageKeys } from '@/lib/i18n';
import { useMessages } from '@/lib/i18n';

import type { Project, ProjectCreate } from './api';
import { createProjectErrorKey, isDuplicateName } from './problems';
import styles from './ProjectsPage.module.css';
import { useCreateProject } from './queries';

/** `ProjectCreateRequest` field bounds, from `projects/schemas.py`. TWIN: that file. */
const NAME_MAX = 200;

type ProjectsKey = MessageKeys['projects'];

/**
 * The roles that may create a project.
 *
 * TWIN: `ACTION_MIN_LEVEL[Action.create_project]` in `backend/src/kalqa/core/authz.py`, whose
 * floor is `MANAGER`. A floor lowered there is a role to add here.
 */
const MAY_CREATE: ReadonlySet<string> = new Set(['admin', 'manager']);

export interface NewProjectDrawerProps {
  readonly onClose: () => void;
  /** Called with the new project instead of `onClose` when the create succeeds. */
  readonly onCreated?: ((project: Project) => void) | undefined;
}

export function NewProjectDrawer({ onClose, onCreated }: NewProjectDrawerProps) {
  const t = useMessages('projects');
  const create = useCreateProject();
  const { data: me } = useMe();
  const activeWorkspaceId = useActiveWorkspaceId();

  // `isCompany` before the role check, and it is not only about the blank `<option>` a nameless
  // workspace would draw. An unclaimed workspace must contain no projects — that is the property
  // §D rule 4 justifies its routing with, and the one retro rule 9 says has to be *checked*
  // rather than assumed — so offering it here as a destination would be the client proposing
  // the write that makes the sentence false. A person whose own workspace is unclaimed and who
  // works for a claimed studio gets that studio; somebody with no company at all gets no picker
  // and lands on B0, which is where founding one happens.
  const candidates = (me?.workspaces ?? [])
    .filter(isCompany)
    .filter((workspace) => MAY_CREATE.has(workspace.role));
  const [workspaceId, setWorkspaceId] = useState<string | null>(
    // The company the chrome is showing, when the caller may file a project there. Otherwise
    // the first they may — somebody who is a plain `member` of their own studio and a manager
    // of one other has exactly one real answer, and defaulting to the wrong one would make
    // the first submission a `403`.
    () =>
      candidates.find((workspace) => workspace.id === activeWorkspaceId)?.id ??
      candidates[0]?.id ??
      activeWorkspaceId,
  );

  const [name, setName] = useState('');

  const [nameError, setNameError] = useState<ProjectsKey | null>(null);
  const [formError, setFormError] = useState<ProjectsKey | null>(null);

  /** The submit guard. A ref rather than state: it has to be true before the next render. */
  const inFlight = useRef(false);
  /** Null until a submission is in progress; cleared by any edit. See the module note. */
  const idempotencyKey = useRef<string | null>(null);

  /** Every field change invalidates the key and clears the error the last attempt left. */
  function edited(setter: (value: string) => void): (value: string) => void {
    return (value) => {
      idempotencyKey.current = null;
      setter(value);
      setNameError(null);
      setFormError(null);
    };
  }

  /**
   * The name and nothing else. No `norm_version_id`, no `phase_definition_ids` — see the
   * module note; the server seeds the studio's default ladder. The workspace travels as the
   * mutation's own argument, not in the body.
   */
  function body(): ProjectCreate {
    return { name: name.trim() };
  }

  async function submit(): Promise<void> {
    if (inFlight.current) return;

    const trimmedName = name.trim();
    if (trimmedName === '') {
      setNameError('drawer.error.nameRequired');
      return;
    }
    if (trimmedName.length > NAME_MAX) {
      setNameError('drawer.error.nameTooLong');
      return;
    }

    setNameError(null);
    setFormError(null);
    idempotencyKey.current ??= crypto.randomUUID();

    inFlight.current = true;
    try {
      const project = await create.mutateAsync({
        body: body(),
        idempotencyKey: idempotencyKey.current,
        workspaceId,
      });
      // `useCreateProject` has already invalidated the list, so closing is all that is left —
      // the new project appears when the refetch lands. Unless the caller wants it: see the
      // module note on why `onCreated` replaces the close rather than following it.
      if (onCreated === undefined) onClose();
      else onCreated(project);
    } catch (error) {
      if (isDuplicateName(error)) {
        setNameError('drawer.error.nameTaken');
        return;
      }
      setFormError(createProjectErrorKey(error));
    } finally {
      inFlight.current = false;
    }
  }

  const busy = create.isPending;

  return (
    <Drawer
      title={t('drawer.title')}
      closeLabel={t('drawer.action.close')}
      onClose={onClose}
      footer={
        <>
          <Button variant="tertiary" disabled={busy} onClick={onClose}>
            {t('drawer.action.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={() => {
              void submit();
            }}
          >
            {t('drawer.action.create')}
          </Button>
        </>
      }
    >
      <p className={styles.drawerSubtitle}>{t('drawer.subtitle')}</p>

      {formError === null ? null : (
        <div className={styles.drawerError}>
          <InlineError variant="block">{t(formError)}</InlineError>
        </div>
      )}

      <form
        className={styles.drawerFields}
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Input
          label={t('drawer.field.name.label')}
          placeholder={t('drawer.field.name.placeholder')}
          // The only field on the panel that is required, and the only one worth the caret.
          autoFocus
          maxLength={NAME_MAX}
          value={name}
          disabled={busy}
          error={nameError === null ? undefined : t(nameError, { max: NAME_MAX })}
          onChange={(event) => {
            edited(setName)(event.target.value);
          }}
        />
        {candidates.length > 1 ? (
          <div className={styles.drawerField}>
            <Select
              label={t('drawer.field.workspace.label')}
              value={workspaceId ?? ''}
              disabled={busy}
              onChange={(event) => {
                idempotencyKey.current = null;
                setWorkspaceId(event.target.value);
              }}
            >
              {candidates.map((workspace) => (
                <option key={workspace.id} value={workspace.id}>
                  {workspace.name}
                </option>
              ))}
            </Select>
            <span className={styles.drawerFieldHint}>{t('drawer.field.workspace.hint')}</span>
          </div>
        ) : null}
        {/* The panel's own submit, so Enter in any field creates the project. The visible
            button lives in the drawer footer, outside this form. */}
        <button type="submit" className="sr-only" disabled={busy}>
          {t('drawer.action.create')}
        </button>
      </form>
    </Drawer>
  );
}
